import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Heart, Users, Plane } from 'lucide-react';
import { useSpaceStore } from '../../stores/spaceStore';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import toast from 'react-hot-toast';

const STORAGE_KEY = 'onboarding-space-draft';

const spaceTypes = [
  { id: 'couple', label: 'Couple', icon: Heart },
  { id: 'family', label: 'Family', icon: Home },
  { id: 'roommates', label: 'Roommates', icon: Users },
  { id: 'trip', label: 'Trip', icon: Plane }
];

const currencies = ['CAD', 'USD', 'EUR', 'GBP', 'MXN', 'AUD', 'JPY'];

const loadDraft = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  return null;
};

const clearDraft = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
};

export default function OnboardingSpace() {
  const navigate = useNavigate();
  const { createSpace } = useSpaceStore();
  const [isLoading, setIsLoading] = useState(false);
  
  const savedDraft = loadDraft();
  const [formData, setFormData] = useState({
    name: savedDraft?.name || '',
    space_type: savedDraft?.space_type || 'couple',
    currency: savedDraft?.currency || 'CAD',
    start_date: savedDraft?.start_date || new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      const hasContent = formData.name || formData.space_type !== 'couple' || formData.currency !== 'CAD';
      if (hasContent) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Please enter a space name');
      return;
    }

    setIsLoading(true);
    try {
      await createSpace({
        name: formData.name,
        space_type: formData.space_type,
        currency: formData.currency,
        start_date: formData.start_date
      }, []);

      clearDraft();
      navigate('/onboarding/members');
    } catch (err: any) {
      toast.error(err.message || 'Failed to create space');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col px-6 py-8 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex-1 flex flex-col max-w-md mx-auto w-full"
      >
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-1 bg-primary rounded-full" />
            <div className="w-8 h-1 bg-muted/30 rounded-full" />
            <div className="w-8 h-1 bg-muted/30 rounded-full" />
            <div className="w-8 h-1 bg-muted/30 rounded-full" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Create Your Space</h1>
          <p className="text-muted">Set up a space to track expenses with your partner or group</p>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col space-y-6">
          <Input
            label="Space Name"
            placeholder="e.g., Our Home, Casa 2026"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            maxLength={100}
            required
          />

          <div className="space-y-3">
            <label className="text-sm font-medium text-muted">Space Type</label>
            <div className="grid grid-cols-2 gap-3">
              {spaceTypes.map(type => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, space_type: type.id })}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-colors ${
                    formData.space_type === type.id
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-card text-foreground hover:border-primary/50'
                  }`}
                >
                  <type.icon size={24} />
                  <span className="font-medium">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium text-muted">Currency</label>
            <div className="flex flex-wrap gap-2">
              {currencies.map(currency => (
                <button
                  key={currency}
                  type="button"
                  onClick={() => setFormData({ ...formData, currency })}
                  className={`px-4 py-2 rounded-full font-medium transition-colors ${
                    formData.currency === currency
                      ? 'bg-primary text-background'
                      : 'bg-card border border-border text-foreground hover:border-primary/50'
                  }`}
                >
                  {currency}
                </button>
              ))}
            </div>
          </div>

          <Input
            label="Start Date"
            type="date"
            value={formData.start_date}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            required
          />

          <div className="flex-1" />

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? 'Creating...' : 'Continue'}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
